import { Button, Input } from '@startupquickstart/frontend';
import { Plus, Trash2 } from 'lucide-react';

interface ImageUrlListProps {
  values: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
  /** Min/max enforced by the API; surface to the admin as helper copy. */
  min?: number;
  max?: number;
  label: string;
  helpText?: string;
}

/**
 * Minimal multi-URL editor for minigame authoring panels (DT-156).
 * Used by slot_machine + memory_match for their image lists. Plain
 * text inputs for now — admins paste URLs from the existing media
 * pipeline. A drag-to-upload integration is a phase-B nice-to-have.
 */
export function ImageUrlList({
  values,
  onChange,
  disabled,
  min,
  max,
  label,
  helpText,
}: ImageUrlListProps) {
  const handleAdd = () => onChange([...values, '']);
  const handleRemove = (index: number) =>
    onChange(values.filter((_, i) => i !== index));
  const handleChange = (index: number, next: string) => {
    const copy = [...values];
    copy[index] = next;
    onChange(copy);
  };

  const atMax = max !== undefined && values.length >= max;
  const belowMin = min !== undefined && values.length < min;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">{label}</span>
        <span className="text-xs text-muted-foreground">
          {values.length}
          {min !== undefined && max !== undefined ? ` / ${min}–${max}` : ''}
        </span>
      </div>
      {helpText && <p className="text-xs text-muted-foreground">{helpText}</p>}
      <div className="space-y-2">
        {values.map((value, index) => (
          <div key={index} className="flex items-center gap-2">
            <Input
              type="url"
              value={value}
              placeholder="https://..."
              onChange={(e) => handleChange(index, e.target.value)}
              disabled={disabled}
            />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={disabled}
              onClick={() => handleRemove(index)}
              aria-label={`Remove ${label} ${index + 1}`}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ))}
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled || atMax}
          onClick={handleAdd}
        >
          <Plus className="h-4 w-4" />
          Add
        </Button>
        {belowMin && (
          <p className="text-xs text-destructive">
            Add at least {min} {label.toLowerCase()} to save.
          </p>
        )}
      </div>
    </div>
  );
}
